import {
  DIGITAL_PRODUCTS_FREE_LISTING_TYPE,
  DIGITAL_PRODUCTS_PAID_LISTING_TYPE,
  FREE_BOOKING_LISTING_TYPE,
  GROUP_SESSION_LISTING_TYPE,
  INDIVIDUAL_SESSION_LISTING_TYPE,
  PAYLATER_BOOKING_LISTING_TYPE,
} from '../../config/configListing';
import { isListingOnSchoolBehalf, LISTING_FIELD_MODE } from './listingFields';

export const LISTING_TYPE = {
  individualSession: INDIVIDUAL_SESSION_LISTING_TYPE,
  groupSession: GROUP_SESSION_LISTING_TYPE,
  freeGroupSession: FREE_BOOKING_LISTING_TYPE,
  payLaterGroupSession: PAYLATER_BOOKING_LISTING_TYPE,
  digitalProductsFree: DIGITAL_PRODUCTS_FREE_LISTING_TYPE,
  digitalProductsPaid: DIGITAL_PRODUCTS_PAID_LISTING_TYPE,
};

export const DIGITAL_PRODUCT_LISTING_TYPES = [
  LISTING_TYPE.digitalProductsFree,
  LISTING_TYPE.digitalProductsPaid,
];

export const DIGITAL_PRODUCTS_LISTING_TYPES_IDENTIFIER = 'digital-products-listing-types';

export const GROUP_SESSION_LISTING_TYPES = [
  LISTING_TYPE.groupSession,
  LISTING_TYPE.freeGroupSession,
  LISTING_TYPE.payLaterGroupSession,
];

export const INDIVIDUAL_SESSION_LISTING_TYPES = [LISTING_TYPE.individualSession];

// Used for identifying the listing type in the listing type config
export const GROUP_SESSION_LISTING_TYPES_IDENTIFIER = 'group-session-listing-types';
export const INDIVIDUAL_SESSION_LISTING_TYPES_IDENTIFIER = 'individual-session-listing-types';

export const isGroupSession = listingType => GROUP_SESSION_LISTING_TYPES.includes(listingType);
export const isIndividualSession = listingType =>
  INDIVIDUAL_SESSION_LISTING_TYPES.includes(listingType);
export const isDigitalProductListingType = listingType =>
  DIGITAL_PRODUCT_LISTING_TYPES.includes(listingType);

const DIGITAL_PRODUCT_FORMAT_MESSAGE_IDS = {
  video: 'ListingPage.digitalProductFormat.video',
  document: 'ListingPage.digitalProductFormat.document',
  audio: 'ListingPage.digitalProductFormat.audio',
  other: 'ListingPage.digitalProductFormat.other',
};

const DIGITAL_PRODUCT_FORMAT_TAG_MESSAGE_IDS = {
  video: 'ListingTags.digitalFormatTag.video',
  document: 'ListingTags.digitalFormatTag.document',
  audio: 'ListingTags.digitalFormatTag.audio',
  other: 'ListingTags.digitalFormatTag.other',
};

const DIGITAL_TYPE_OPTION_KEYS = new Set(Object.keys(DIGITAL_PRODUCT_FORMAT_MESSAGE_IDS));

/**
 * Saved value for digital product type from listing publicData.
 * Flex / hosted listing field id: `digitalProductContentType`. Legacy local key: `digitalProductFormat`.
 */
export const getDigitalProductTypeOption = publicData => {
  const raw =
    publicData?.digitalProductContentType != null && publicData?.digitalProductContentType !== ''
      ? publicData.digitalProductContentType
      : publicData?.digitalProductFormat;
  if (raw == null || raw === '') {
    return null;
  }
  const normalized = String(raw)
    .trim()
    .toLowerCase();
  return DIGITAL_TYPE_OPTION_KEYS.has(normalized) ? normalized : null;
};

/**
 * i18n id for the under-hero digital product type label (falls back to generic digital label).
 */
export const getDigitalProductHeroLabelMessageId = publicData => {
  const format = getDigitalProductTypeOption(publicData);
  return format ? DIGITAL_PRODUCT_FORMAT_MESSAGE_IDS[format] : 'ListingPage.digitalContentLabel';
};

/**
 * i18n id for listing/search tags: digital format only (video, audio, …). Unknown or missing format → other.
 */
export const getDigitalProductFormatTagMessageId = publicData => {
  const format = getDigitalProductTypeOption(publicData);
  return (
    DIGITAL_PRODUCT_FORMAT_TAG_MESSAGE_IDS[format] || DIGITAL_PRODUCT_FORMAT_TAG_MESSAGE_IDS.other
  );
};

// IMPORTANT: When changing sync with src/transactions/transaction.js
export const listingWithPayment = [
  GROUP_SESSION_LISTING_TYPE,
  INDIVIDUAL_SESSION_LISTING_TYPE,
  DIGITAL_PRODUCTS_PAID_LISTING_TYPE,
];
export const listingWithoutPayment = [
  FREE_BOOKING_LISTING_TYPE,
  PAYLATER_BOOKING_LISTING_TYPE,
  DIGITAL_PRODUCTS_FREE_LISTING_TYPE,
];
export const isListingWithPayment = listingType => listingWithPayment.includes(listingType);
export const isListingWithoutPayment = listingType => listingWithoutPayment.includes(listingType);

export const isFreeGroupSession = listingType => listingType === LISTING_TYPE.freeGroupSession;
export const isPayLaterListing = listingType => listingType === PAYLATER_BOOKING_LISTING_TYPE;

export const getListingTypeLabel = (listingType, config) => {
  const listingTypes = config.listing.listingTypes;
  const matchingListingType = listingTypes?.find(type => type.listingType === listingType);
  return matchingListingType ? matchingListingType.label : 'ERROR: LABEL_NOT_FOUND';
};

/**
 * @param {Object} props - Component props or necessary data
 * @param {propTypes.listing} props.listing - The current listing object
 * @returns {propTypes.listingTypeInfo} - object with listing type information
 **/
export const getListingTypeInfo = ({ listing }) => {
  if (!listing) {
    return null;
  }

  const { publicData = {} } = listing.attributes;
  const { listingType, mode } = publicData;

  return {
    isBookingViaSoulwiseNotAvailable: isListingOnSchoolBehalf(listing),
    isPayLaterListing: isPayLaterListing(listingType),
    isFreeListing: isFreeGroupSession(listingType),
    isPaidListing: isListingWithPayment(listingType),
    isIndividualListing: isIndividualSession(listingType),
    isOnlineListing: mode === LISTING_FIELD_MODE.online,
    isDigitalProductListing: isDigitalProductListingType(listingType),
  };
};
